<template>
  <div
    v-click-outside="hideSidebar"
    class="info-sidebar-main-wrap"
    :class="{ 'border-0 border-t border-solid border-dividers-default': isInbox }"
    :style="infoSidebarStyle"
  >
    <div class="info-sidebar-wrap">
      <div class="sidebar-header p-6">
        <div class="flex flex-col gap-4">
          <div class="h-6 flex whitespace-nowrap">
            <span class="flex items-center text-sm font-medium min-w-[75px]">
              {{ translate('created') }}
            </span>
            <span class="text-sm flex items-center">
              {{ timeCreated }}
            </span>
            <span class="text-xs flex items-center font-normal text-text-placeholder ml-2">
              {{ dateCreated }}
            </span>
          </div>
          <div class="h-6 flex whitespace-nowrap">
            <span class="flex items-center text-sm font-medium min-w-[75px]">
              {{ translate('updated') }}
            </span>
            <UserAvatar
              class="mr-2"
              :member="modifiedMember"
            />
            <span class="text-sm flex items-center">
              {{ timeUpdated }}
            </span>
            <span class="text-xs flex items-center font-normal text-text-placeholder ml-2 mr-0">
              {{ dateUpdated }}
            </span>
          </div>
        </div>
      </div>
      <div class="sidebar-body">
        <ProjectDescriptionInfoSidebar v-if="isProject || isManageSpaces" />
        <Reporting v-if="displayReporting" />
        <InfoSidebarListOfMembers
          v-if="isProject || isManageSpaces || isBoard"
          :members="listOfUsersToDisplay"
        />
      </div>
    </div>
    <MemberDropdownWrap
      v-if="displayAddMembersDropdown"
      :entity="$store.getters.getCurrentProject"
      :project-members="listOfUsersToDisplay"
    />
  </div>
</template>

<script>
import UserAvatar from '@/components/widgets/UserAvatar';
import InfoSidebarListOfMembers from '@/components/widgets/InfoSidebarListOfMembers';
import { timeDifference } from '@/utilities/timeDifference';
import headerMixin from '@/mixins/headerMixin';
import ProjectDescriptionInfoSidebar from '@/components/widgets/ProjectDescriptionInfoSidebar';
import Reporting from '@/components/widgets/Reporting.vue';
import MemberDropdownWrap from '@/components/widgets/MemberDropdownWrap';

export default {
  name: 'InfoSidebar',
  components: {
    UserAvatar,
    InfoSidebarListOfMembers,
    ProjectDescriptionInfoSidebar,
    Reporting,
    MemberDropdownWrap,
  },
  mixins: [headerMixin],
  props: {
    modifiedMemberId: {
      type: String,
      required: false,
      default: '',
    },
    members: {
      type: Array,
      require: false,
      default: () => [],
    },
    postedDate: {
      type: Number,
      required: false,
      default: 0,
    },
    modifiedDate: {
      type: Number,
      required: false,
      default: 0,
    },
    activities: {
      type: Array,
      require: false,
      default: () => [],
    },
  },
  data() {
    return {
      listenersAdded: false,
    };
  },

  computed: {
    infoSidebarStyle() {
      return {
        right: this.isAnyCardDisplayedInDockedLayout ? `${this.cardWidth}px` : '0',
      };
    },
    isAnyCardDisplayedInDockedLayout() {
      return this.$store.getters.getIsAnyCardDisplayedInLayout(this.$constants.docked);
    },
    cardWidth() {
      return this.$store.getters.getCardWidth;
    },
    routeOrInboxResourceChanged() {
      return this.isInbox ? this.currentInboxResource : this.$route;
    },
    meId() {
      return this.$store.getters.getUserId;
    },
    timeCreated() {
      return this.getTimeDifference(this.postedDate * 1000, 1);
    },
    timeUpdated() {
      return this.getTimeDifference(this.modifiedDate * 1000, 1);
    },
    dateCreated() {
      const dateCreated = new Date(Number(this.postedDate) * 1000);
      const months = this.translate('monthsAbbreviation').split(',');
      const day = dateCreated.getDate();
      const month = months[dateCreated.getMonth()];
      const year = dateCreated.getFullYear();
      return `${day} ${month} ${year}`;
    },
    dateUpdated() {
      const dateCreated = new Date(Number(this.modifiedDate) * 1000);
      const months = this.translate('monthsAbbreviation').split(',');
      const day = dateCreated.getDate();
      const month = months[dateCreated.getMonth()];
      const year = dateCreated.getFullYear();
      const d = new Date();
      const currentYear = d.getFullYear();
      if (year < currentYear) {
        return `${day} ${month} ${year}`;
      } else {
        return `${day} ${month}`;
      }
    },
    modifiedMember() {
      const member = this.$store.getters.getMemberById(this.modifiedMemberId);
      return Object.keys(member).length ? member : { id: this.modifiedMemberId };
    },
    listOfUsersToDisplay() {
      return this.isProject || this.isManageSpaces
        ? this.currentProjectMembers
        : this.currentBoardAssignees;
    },
    currentProjectMembers() {
      const sortedMembers = [...this.members].sort((a, b) => a.assigned_date - b.assigned_date);

      const meIndex = sortedMembers.findIndex((member) => member.user_id === this.meId);
      if (meIndex !== -1) {
        const me = sortedMembers.splice(meIndex, 1);
        sortedMembers.unshift(me[0]);
      }

      return sortedMembers;
    },
    currentBoardAssignees() {
      const currentBoardAssignees = [];
      const { list_order } = this.$store.getters.currentBoard;
      list_order?.forEach((listId) => {
        const cardIds = this.$store.getters.lists[listId].card_order || [];
        cardIds.forEach((cardId) => {
          const cards = this.$store.getters.getCards || {};
          const cardMembers = cards[cardId]?.members || [];
          cardMembers.forEach((cardMember) => {
            if (
              !currentBoardAssignees.some(
                (boardAssignee) => boardAssignee.user_id === cardMember.user_id
              )
            ) {
              cardMember.user_id === this.meId
                ? currentBoardAssignees.unshift(cardMember)
                : currentBoardAssignees.push(cardMember);
            }
          });
        });
      });

      return currentBoardAssignees;
    },
    displayAddMembersDropdown() {
      return (
        (this.isProject || this.isManageSpaces) &&
        this.$store.getters.getCurrentCardIdMemberDropdown === 'project' &&
        this.$store.getters.getAddMembersTarget.type === this.$constants.infoSidebarProject
      );
    },
    currentInboxResource() {
      return this.$store.getters.getCurrentInboxResource;
    },
    currentInboxResourceType() {
      return this.currentInboxResource.resource_type || '';
    },
    displayReporting() {
      return this.isBoard || (this.isInbox && this.currentInboxResourceType === 'board');
    },
  },
  watch: {
    routeOrInboxResourceChanged() {
      this.$store.dispatch('displayInfoSidebar', false);
    },
  },
  activated() {
    this.addEventListeners();
  },
  deactivated() {
    this.removeEventListeners();
  },

  mounted() {
    this.addEventListeners();
  },

  beforeUnmount() {
    this.removeEventListeners();
  },

  methods: {
    getTimeDifference(time, depth, dueDate) {
      return time ? timeDifference(time, depth, dueDate) : '';
    },
    hideSidebar(e) {
      if (!e.target.closest('.item-row')) {
        this.$store.dispatch('displayInfoSidebar', false);
        this.$store.dispatch('setSelectedProjectId', '');
      }
    },
    checkKey($event) {
      if ($event.key === 'Escape') {
        $event.stopPropagation();
        this.$store.dispatch('displayInfoSidebar', false);
      }
    },

    addEventListeners() {
      if (this.listenersAdded) return;
      window.addEventListener('keydown', this.checkKey);
      this.listenersAdded = true;
    },

    removeEventListeners() {
      if (!this.listenersAdded) return;
      window.removeEventListener('keydown', this.checkKey);
      this.listenersAdded = false;
    },
  },
};
</script>

<style lang="scss">
.info-sidebar-main-wrap {
  @apply h-full-minus-header absolute flex z-20 top-header-H;

  .info-sidebar-wrap {
    @apply overflow-hidden border-0 border-l border-solid border-dividers-default;
    @apply w-infoSidebar-W flex flex-col bg-background-strongest text-text-default;

    .sidebar-header {
      @apply border-0 border-b border-solid border-dividers-default;
    }

    .sidebar-body {
      @apply flex flex-col overflow-hidden;

      & > *:not(:last-child) {
        @apply border-b border-0 border-solid border-dividers-default;
      }
    }
  }
}
</style>
